import { format } from 'date-fns'

import { httpClient } from '@/services/http/httpClient'
import type { FileType } from '@/types/fileType'
import type { IdeaFieldFormat } from '@/types/ideaFieldsFormat'
import type { Option } from '@/types/option'
import { generateSlug } from '@/utils/generateSlug'
import type { IdeaStatus } from '../types'

export type IdeaHttpResponse = {
  id: string
  title: string
  description: string
  unread: {
    comments: number
    registries: number
  }
  lastSeenAt: {
    comments: string | null
    registries: string | null
  } | null
  isEditable: boolean
  isExcludable: boolean
  ideaStatuses: {
    name: string
    customName: string | null
    color: string
    customColor: string | null
  }
  likes: number
  sequence: number
  createdAt: string
  ideaFiles: string[]
  processActivity?: {
    id: string
    name: string
  }
  externalLink: {
    id: string
    url: string
  }[]
  ideaFields: {
    id: string
    title: string
    name: string
    type: IdeaFieldFormat
    options?: { name: string }[]
    ideaFieldValues: {
      id: string
      value: string
    }[]
  }[]
  ideaUsers: {
    type: string
    status: string
    user: {
      id: string
      name: string
      email: string
      image?: string
      status: string
      area?: {
        id: string
        name: string
      }
    }
  }[]
  ideaTags: {
    tag: {
      id: string
      name: string
      textColor: string
      color: string
    }
  }[]
  campaign: {
    id: string
    sequence: string
    title: string
  }
  secondaryLinks: {
    id: string
    sequence: string
    title: string
  }[]
  kanbanStepId: string
  ideaApprovals: {
    id: string
    ideaId: string
    approvedBy: string
    description: string
    createdAt: string
    approvedByUser: {
      id: string
      name: string
      email: string
      image: string | null
    }
  } | null
  ideaRecuse: {
    id: string
    ideaId: string
    recusedById: string
    description: string
    createdAt: string
    updatedAt: string
    recusedByUser: {
      id: string
      name: string
      email: string
      image: string | null
    }
  } | null
}

type User = {
  id: string
  name: string
  email: string
  image?: string
  status?: string
  area?: {
    id: string
    name: string
  }
}

export type Idea = {
  id: string
  title: string
  sequence: string
  unread: {
    comments: number
    registries: number
  }
  lastSeenAt: {
    comments: string | null
    registries: string | null
  }
  description: string
  isEditable: boolean
  isExcludable: boolean
  status: IdeaStatus
  createdAt: string
  author: User
  participants: {
    id: string
    name: string
    email: string
    image?: string
    area?: {
      id: string
      name: string
    }
    status: string
  }[]
  linkedIdeas: (Option & { title: string })[]
  file?: FileType[]
  challenge: {
    id: string
    sequence: string
    title: string
  }
  fields: {
    id: string
    title: string
    slug: string
    type: IdeaFieldFormat
    options?: { name: string }[]
    values: {
      id: string
      value: string
    }[]
  }[]
  links: Option[]
  tags: {
    id: string
    text: string
    textColor: string
    backgroundColor: string
  }[]
  workflow: {
    id: string
    name: string
  }
  kanbanStepId: string

  ideaApprovals: {
    id: string
    ideaId: string
    approvedBy: string
    description: string
    createdAt: string
    approvedByUser: {
      id: string
      name: string
      email: string
      image: string | null
    }
  } | null
  ideaRecuse: {
    id: string
    ideaId: string
    description: string
    createdAt: string
    recusedByUser: {
      id: string
      name: string
      email: string
      image: string | null
    }
  } | null
}

export async function getIdea(id: string): Promise<Idea> {
  const { data } = await httpClient.get<IdeaHttpResponse>(`/ideas/ideas/${id}`)

  const authorIdeaUser = data?.ideaUsers.find(
    (ideaUser) => ideaUser.type === 'OWNER',
  )

  if (!authorIdeaUser) {
    throw new Error('Idea Author not found')
  }

  const author = authorIdeaUser.user

  const idea = {
    id: data.id,
    title: data.title,
    sequence: `#${String(data.sequence).padStart(6, '0').replace('#', '')}`,
    description: data.description,
    status: data.ideaStatuses.name,
    isEditable: data.isEditable,
    isExcludable: data.isExcludable,
    createdAt: format(new Date(data.createdAt), 'dd/MM/yyyy'),
    author,
    ...(data.ideaFiles.length && {
      file: data.ideaFiles.map((file) => ({
        preview: file,
      })),
    }),
    challenge: {
      id: data.campaign.id,
      sequence: `#${String(data.campaign.sequence).padStart(2, '0')}`,
      title: data.campaign.title,
    },
    fields: data.ideaFields.map((ideaField) => ({
      id: ideaField.id,
      title: ideaField.title,
      slug: generateSlug(ideaField.name),
      type: ideaField.type,
      options: ideaField.options?.map((option) => ({
        name: option.name,
      })),
      values: ideaField.ideaFieldValues.map((ideaFieldValue) => ({
        id: ideaFieldValue.id,
        value: ideaFieldValue.value,
      })),
    })),
    linkedIdeas: data.secondaryLinks.map((secondaryLink) => ({
      value: secondaryLink.id,
      label: `#${String(secondaryLink.sequence).padStart(6, '0')}`,
      title: secondaryLink.title,
    })),
    links: data.externalLink.map((externalLink) => ({
      value: externalLink.id,
      label: externalLink.url,
    })),
    participants: data.ideaUsers
      .filter((ideaUser) => ideaUser.type !== 'OWNER')
      .map((ideaUser) => {
        return {
          id: ideaUser.user.id,
          name: ideaUser.user.name,
          email: ideaUser.user.email,
          image: ideaUser.user.image,
          area: ideaUser.user.area,
          status: ideaUser.user.status,
        }
      }),
    tags: data.ideaTags.map((ideaTag) => ({
      id: ideaTag.tag.id,
      text: ideaTag.tag.name,
      textColor: ideaTag.tag.textColor,
      backgroundColor: ideaTag.tag.color,
    })),
    ...(data.processActivity && {
      workflow: {
        id: data.processActivity.id,
        name: data.processActivity.name,
      },
    }),
    kanbanStepId: data.kanbanStepId,
    ideaApprovals: data.ideaApprovals ?? null,
    ideaRecuse: data.ideaRecuse ?? null,
    unread: data.unread ?? { comments: 0, registries: 0 },
    lastSeenAt: data.lastSeenAt ?? { comments: null, registries: null },
  } as Idea

  return idea
}
